import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  getAccountingDocuments,
  getAccountingDocumentRequests,
  getAccountingSourceData,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import { getSalonTimezone } from "../../utils/salonTimezone";
import {
  accountingContextFromSearchParams,
  accountingPath,
  hasAccountingContext,
} from "./accountingRouteContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";
import {
  currentMonthValue,
  money,
  monthRange,
  monthValueFromDate,
  requestMatchesMonth,
} from "./accountingDate";

const openStatuses = new Set(["needed", "needs_new_copy"]);
const uploadedStatuses = new Set(["received", "being_reviewed", "done"]);

function uploadPath(request, context) {
  return accountingPath("/documents", context, {
    document_type: request.document_type,
    request_id: String(request.id),
  });
}

export default function SalonBusinessCpaMonthlyReview() {
  const { token, currentSalon, user } = useAuth();
  const [searchParams] = useSearchParams();
  const accountingContext = useMemo(
    () => accountingContextFromSearchParams(searchParams),
    [searchParams]
  );
  const accountantView = isSalonBusinessCpaAccountant(user);
  const scopedAccountantView = accountantView && hasAccountingContext(accountingContext);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon || { salon_timezone: user?.salon_timezone }),
    [currentSalon, user?.salon_timezone]
  );
  const [selectedMonth, setSelectedMonth] = useState(() => currentMonthValue(salonTimezone));
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [sourceData, setSourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const range = useMemo(() => monthRange(selectedMonth), [selectedMonth]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [requestData, documentData, source] = await Promise.all([
          getAccountingDocumentRequests(token, accountingContext),
          getAccountingDocuments(token, accountingContext),
          getAccountingSourceData(token, {
            ...accountingContext,
            start_date: range.start_date,
            end_date: range.end_date,
          }).catch(() => null),
        ]);
        if (cancelled) return;
        setRequests(Array.isArray(requestData?.requests) ? requestData.requests : []);
        setDocuments(Array.isArray(documentData?.documents) ? documentData.documents : []);
        setSourceData(source);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "Unable to load this monthly review.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [accountingContext, range.end_date, range.start_date, token]);

  const monthRequests = useMemo(
    () =>
      requests.filter(
        (request) =>
          request.status !== "cancelled" &&
          requestMatchesMonth(request, selectedMonth, salonTimezone)
      ),
    [requests, selectedMonth, salonTimezone]
  );

  const monthDocuments = useMemo(
    () =>
      documents.filter(
        (document) => monthValueFromDate(document.created_at_utc, salonTimezone) === selectedMonth
      ),
    [documents, selectedMonth, salonTimezone]
  );

  const missing = monthRequests.filter((request) => openStatuses.has(request.status));
  const uploaded = monthDocuments.filter((document) => uploadedStatuses.has(document.status));
  const reviewing = [
    ...monthRequests.filter((request) => request.status === "being_reviewed"),
    ...monthDocuments.filter((document) => document.status === "being_reviewed"),
  ];
  const done = [
    ...monthRequests.filter((request) => request.status === "done"),
    ...monthDocuments.filter((document) => document.status === "done"),
  ];
  const sourceSales = sourceData?.sales_totals || {};

  return (
    <SalonBusinessCpaShell
      title="Monthly review"
      description={
        scopedAccountantView
          ? "Review this assigned client's monthly records, source totals, and open items."
          : "Choose a month, upload missing records, and see what is ready for accounting review."
      }
      actions={
        <label className="text-sm font-semibold text-slate-700">
          Month
          <input
            type="month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            className="mt-2 h-11 rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
          />
        </label>
      }
    >
      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-5">
        {[
          ["Missing records", missing.length],
          ["Uploaded", uploaded.length],
          ["In review", reviewing.length],
          ["Done", done.length],
          ["Sales", money(sourceSales.sales_before_tax)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Taxes & reports</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Open the workspace that matches what you are reviewing this month.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[
            ["/gst-hst", "GST/HST"],
            ["/payroll", "Payroll"],
            ["/bookkeeping", "Bookkeeping"],
            ["/income-taxes", "Income taxes"],
            ["/financial-statements", "Financial statements"],
          ].map(([path, label]) => (
            <Link
              key={path}
              to={accountingPath(path, accountingContext)}
              className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 text-center text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
            >
              {label}
            </Link>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">
                Records needed for {range.label || "this month"}
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Upload these first so review can keep moving.
              </p>
            </div>
            <Link
              to={accountingPath("/requests", accountingContext)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              View all requests
            </Link>
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                Loading monthly review...
              </div>
            ) : missing.length ? (
              missing.map((request) => (
                <article key={request.id} className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-950">{request.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-700">
                        {request.client_note || "Please upload this record for review."}
                      </p>
                      {request.has_unanswered_questions ? (
                        <p className="mt-2 text-xs font-semibold text-amber-700">
                          A few short answers are also needed.
                        </p>
                      ) : null}
                    </div>
                    <Link
                      to={
                        accountantView
                          ? accountingPath("/requests", accountingContext)
                          : uploadPath(request, accountingContext)
                      }
                      className={`inline-flex min-h-10 shrink-0 items-center justify-center rounded-full px-4 text-sm font-semibold ${
                        accountantView
                          ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          : "bg-rose-500 text-white hover:bg-rose-600"
                      }`}
                    >
                      {accountantView ? "Open request" : "Upload"}
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-700">
                No missing records are showing for this month.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">
            Ellyanna source data
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            These totals come from Ellyanna Booking when available and help prepare the review.
          </p>
          <dl className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Sales before tax</dt>
              <dd className="font-semibold text-slate-950">{money(sourceSales.sales_before_tax)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Tax collected</dt>
              <dd className="font-semibold text-slate-950">{money(sourceSales.tax_collected)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Tickets</dt>
              <dd className="font-semibold text-slate-950">{Number(sourceSales.ticket_count || 0)}</dd>
            </div>
          </dl>
          <Link
            to={accountingPath("/data", accountingContext, {
              start_date: range.start_date,
              end_date: range.end_date,
            })}
            className="mt-5 inline-flex min-h-10 items-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Open source data
          </Link>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-950">Uploaded this month</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {uploaded.length ? (
            uploaded.slice(0, 8).map((document) => (
              <article key={document.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-semibold text-slate-950">{document.file_name}</h3>
                <p className="mt-1 text-sm text-slate-600">
                  {document.document_type_label} - {document.status_label}
                </p>
              </article>
            ))
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
              No records uploaded for this month yet.
            </div>
          )}
        </div>
      </section>
    </SalonBusinessCpaShell>
  );
}
